import { api } from './api';
import { platformSaasBillingOperationsService } from './platformSaasBillingOperationsService';

const PLATFORM_SAAS_BILLING_BASE = '/api/platform-admin/saas-billing';
const RUNTIME_BASE = `${PLATFORM_SAAS_BILLING_BASE}/runtime`;
const RUNTIME_EXECUTIONS_BASE = `${RUNTIME_BASE}/executions`;

const buildQueryParams = (params = {}) => {
  const query = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') {
      return;
    }
    query[key] = value;
  });
  return query;
};

export const runtimeSchedulerService = {
  getDiagnostics: (params) => api.get(`${RUNTIME_BASE}/scheduler/diagnostics`, { params: buildQueryParams(params) }),
  getHealth: (params) => api.get(`${RUNTIME_BASE}/scheduler/health`, { params: buildQueryParams(params) }),
  getRuns: (params) => api.get(`${RUNTIME_BASE}/scheduler/runs`, { params: buildQueryParams(params) }),
};

export const runtimeDiagnosticsService = {
  getMetrics: (params) => api.get(`${RUNTIME_BASE}/diagnostics/metrics`, { params: buildQueryParams(params) }),
  getRecovery: (params) => api.get(`${RUNTIME_BASE}/diagnostics/recovery`, { params: buildQueryParams(params) }),
  getRecoveryStuckRequests: (params) =>
    api.get(`${RUNTIME_BASE}/diagnostics/recovery/stuck-requests`, { params: buildQueryParams(params) }),
  getRecoveryAgingBuckets: (params) =>
    api.get(`${RUNTIME_BASE}/diagnostics/recovery/aging-buckets`, { params: buildQueryParams(params) }),
  getRecoverySummary: (params) =>
    api.get(`${RUNTIME_BASE}/diagnostics/recovery/summary`, { params: buildQueryParams(params) }),
  getRetryPreview: (params) => api.get(`${RUNTIME_BASE}/diagnostics/retry-preview`, { params: buildQueryParams(params) }),
};

export const runtimeRecoveryOperationsService = {
  replaySafe: (requestId, payload) =>
    api.post(`${RUNTIME_BASE}/diagnostics/recovery/requests/${encodeURIComponent(String(requestId || '').trim())}/replay-safe`, payload),
  markDeadLetter: (requestId, payload) =>
    api.post(`${RUNTIME_BASE}/diagnostics/recovery/requests/${encodeURIComponent(String(requestId || '').trim())}/mark-dead-letter`, payload),
  reopenDeadLetter: (requestId, payload) =>
    api.post(`${RUNTIME_BASE}/diagnostics/recovery/requests/${encodeURIComponent(String(requestId || '').trim())}/reopen-dead-letter`, payload),
  reclassify: (requestId, payload) =>
    api.post(`${RUNTIME_BASE}/diagnostics/recovery/requests/${encodeURIComponent(String(requestId || '').trim())}/reclassify`, payload),
  reissuePreparePayment: (requestId, payload) =>
    api.post(`${RUNTIME_BASE}/diagnostics/recovery/requests/${encodeURIComponent(String(requestId || '').trim())}/reissue-prepare-payment`, payload),
  getPaymentCaseTimeline: (params) => platformSaasBillingOperationsService.getPaymentCaseTimeline(buildQueryParams(params)),
};

export const runtimeHistoryService = {
  listExecutions: (params) => api.get(RUNTIME_EXECUTIONS_BASE, { params: buildQueryParams(params) }),
  searchExecutionsByCorrelationId: (params) =>
    api.get(`${RUNTIME_EXECUTIONS_BASE}/search`, { params: buildQueryParams(params) }),
  getExecutionDetail: (executionId) => api.get(`${RUNTIME_EXECUTIONS_BASE}/${executionId}`),
  getExecutionTimeline: (executionId) =>
    api.get(`${RUNTIME_EXECUTIONS_BASE}/${executionId}/timeline`),
  // Runtime Search convenience endpoint wrapper.
  searchRuntimeByCorrelation: (correlationId, params = {}) =>
    api.get(`${RUNTIME_EXECUTIONS_BASE}/search`, {
      params: buildQueryParams({ ...params, correlation_id: correlationId }),
    }),
  // Webhook bridge history is represented as runtime executions filtered by operation_type.
  listWebhookBridgeExecutions: (params = {}) =>
    api.get(RUNTIME_EXECUTIONS_BASE, {
      params: buildQueryParams({ ...params, operation_type: params.operation_type || 'webhook_bridge' }),
    }),
};

export const runtimeRetryService = {
  getPlanPreview: (params) => api.get(`${RUNTIME_BASE}/retry-plan/preview`, { params: buildQueryParams(params) }),
  validatePlan: (payload) => api.post(`${RUNTIME_BASE}/retry-plan/validate`, payload),
  executePlanDryRun: (payload) =>
    api.post(`${RUNTIME_BASE}/retry-plan/execute-dry-run`, payload),
  executePlanMock: (payload) =>
    api.post(`${RUNTIME_BASE}/retry-plan/execute-mock`, payload),
};

export const runtimeReconciliationService = {
  getByExecution: (executionId) =>
    api.get(`${RUNTIME_BASE}/reconciliation/execution/${executionId}`),
  getByInvoice: (invoiceId) =>
    api.get(`${RUNTIME_BASE}/reconciliation/invoice/${invoiceId}`),
  getByCharge: (chargeId) =>
    api.get(`${RUNTIME_BASE}/reconciliation/charge/${chargeId}`),
  getByTenant: (tenantId) =>
    api.get(`${RUNTIME_BASE}/reconciliation/tenant/${tenantId}`),
};

export const runtimeProviderConfigService = {
  listProviders: () => api.get(`${PLATFORM_SAAS_BILLING_BASE}/providers`),
  getGlobalConfig: () => api.get(`${PLATFORM_SAAS_BILLING_BASE}/providers/config/global`),
  updateGlobalConfig: (payload) =>
    api.put(`${PLATFORM_SAAS_BILLING_BASE}/providers/config/global`, payload),
  updateGlobalDefault: (payload) =>
    api.put(`${PLATFORM_SAAS_BILLING_BASE}/providers/config/global-default`, payload),
  getTenantConfig: (tenantId) =>
    api.get(`${PLATFORM_SAAS_BILLING_BASE}/tenants/${tenantId}/provider-config`),
  updateTenantConfig: (tenantId, payload) =>
    api.put(`${PLATFORM_SAAS_BILLING_BASE}/tenants/${tenantId}/provider-config`, payload),
  deleteTenantConfig: (tenantId) =>
    api.delete(`${PLATFORM_SAAS_BILLING_BASE}/tenants/${tenantId}/provider-config`),
};

export const runtimeToolsService = {
  previewTenant: (tenantId) =>
    api.post(`${RUNTIME_BASE}/tenants/${tenantId}/preview`),
  runInvoiceDryRun: (tenantId) =>
    api.post(`${RUNTIME_BASE}/tenants/${tenantId}/invoices/dry-run`),
  listTenantInvoices: (tenantId) =>
    api.get(`${RUNTIME_BASE}/tenants/${tenantId}/invoices`),
  runChargeDryRun: (tenantId, invoiceId) =>
    api.post(`${RUNTIME_BASE}/tenants/${tenantId}/invoices/${invoiceId}/charges/dry-run`),
  runCharge: (tenantId, invoiceId, payload) =>
    api.post(`${RUNTIME_BASE}/tenants/${tenantId}/invoices/${invoiceId}/charges`, payload),
  listTenantCharges: (tenantId) =>
    api.get(`${RUNTIME_BASE}/tenants/${tenantId}/charges`),
  listInvoiceCharges: (tenantId, invoiceId) =>
    api.get(`${RUNTIME_BASE}/tenants/${tenantId}/invoices/${invoiceId}/charges`),
  syncChargeMockStatus: (tenantId, chargeId, payload) =>
    api.post(`${RUNTIME_BASE}/tenants/${tenantId}/charges/${chargeId}/sync-mock-status`, payload),
  getChargeProviderStatus: (tenantId, chargeId) =>
    api.get(`${RUNTIME_BASE}/tenants/${tenantId}/charges/${chargeId}/provider-status`),
  previewCycleEligible: (payload) =>
    api.post(`${RUNTIME_BASE}/cycle/preview-eligible`, payload),
  runCycleDryRun: (payload) =>
    api.post(`${RUNTIME_BASE}/cycle/run-dry-run`, payload),
  runLifecycleMaterializerDryRun: (payload) =>
    api.post(`${RUNTIME_BASE}/lifecycle-materializer/run-dry-run`, payload),
  getReplayByExecution: (executionId) =>
    api.get(`${RUNTIME_BASE}/replay/preview/execution/${executionId}`),
  getReplayByItem: (runtimeExecutionItemId) =>
    api.get(`${RUNTIME_BASE}/replay/preview/item/${runtimeExecutionItemId}`),
};

export const runtimeOperationalService = {
  runLifecycleMaterializerDryRun: (payload) => runtimeToolsService.runLifecycleMaterializerDryRun(payload),
};

const runtimeOperationalApi = {
  runtimeOperationalService,
  runtimeSchedulerService,
  runtimeDiagnosticsService,
  runtimeRecoveryOperationsService,
  runtimeHistoryService,
  runtimeRetryService,
  runtimeReconciliationService,
  runtimeProviderConfigService,
  runtimeToolsService,
};

export default runtimeOperationalApi;